'use client';

/**
 * useFocusTrap — the single, shared focus-trap for Salonomy's modal surfaces (drawers, dialogs).
 *
 * Written once here so the console AppShell drawer and the marketing MobileNav drawer (and the
 * Modal) share ONE implementation instead of the two near-identical copies the mobile remediation
 * left behind. While `active`:
 *  - moves focus to the first focusable element inside `ref` (so a keyboard/AT user lands in the
 *    surface, not stranded behind it),
 *  - traps Tab / Shift+Tab inside `ref` (it is `aria-modal`),
 *  - calls `onEscape` on Escape,
 *  - on deactivation, restores focus to whatever was focused before the trap opened (typically the
 *    trigger button) — so the keyboard user returns exactly where they were.
 *
 * This is a hook, not a component, so it adds no DOM and no wrapper; the consuming island keeps its
 * own `'use client'` boundary (marketing) or runs as-is (console SPA).
 */
import { useEffect } from 'react';
import type { RefObject } from 'react';

/** Tabbable elements inside the trapped container, in DOM order. */
// Includes form fields (input/select/textarea) so a dialog whose only interactive content is an input
// — e.g. the command palette (a search field + role=option rows) — still traps Tab; without these the
// trap would have nothing to cycle and focus would escape the aria-modal.
const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface FocusTrapOptions {
  /**
   * Restore focus to the element that was focused before activation when the trap deactivates.
   * Defaults to true. Pass a ref to force focus to a specific element instead (e.g. the trigger).
   */
  restoreFocus?: boolean | RefObject<HTMLElement | null>;
}

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  onEscape: () => void,
  options: FocusTrapOptions = {},
): void {
  const { restoreFocus = true } = options;

  useEffect(() => {
    if (!active) return;
    const container = ref.current;
    // Remember where focus was so we can return the user there on close.
    const previouslyFocused = document.activeElement as HTMLElement | null;

    // Move focus into the surface (first focusable element).
    container?.querySelector<HTMLElement>(FOCUSABLE_SELECTOR)?.focus();

    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onEscape();
        return;
      }
      if (e.key !== 'Tab') return;
      const focusables = ref.current?.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
      if (!focusables || focusables.length === 0) return;
      const list = Array.from(focusables);
      const first = list[0]!;
      const last = list[list.length - 1]!;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (restoreFocus === false) return;
      const target =
        restoreFocus === true ? previouslyFocused : (restoreFocus.current ?? previouslyFocused);
      target?.focus();
    };
  }, [ref, active, onEscape, restoreFocus]);
}
